import React, { useContext, useEffect, useState } from "react";
import { Table, Tag, Input, Popconfirm } from "antd";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import client from "../../api";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { getErrorMessage } from "src/lib/utils";
import GlobalContext from "src/context/global-context";

interface CardType {
  id: number;
  product_id: number;
  name: string;
  bank_type_id: number;
  color_hex: string;
  card_logo_name: string | null;
  card_text_color: string;
  product: {
    id: number;
    code: string;
    name: string;
  };
  asset: {
    id: number;
    code: string;
  };
}

const CardTypes = () => {
  const { canView, canList, canAdd, canEdit, canDelete } = usePermissions(
    permissionConstants.CARD_DESIGN
  );
  const canViewCardTypes = canView || canList;

  const [dataSource, setDataSource] = useState<CardType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  const navigate = useNavigate();

  // LIST API
  const { mutate: getCardTypes, isLoading } = useMutation(
    client.cardTypes?.list,
    {
      onSuccess: (res: any) => {
        const response = res?.data;

        setDataSource(
          (response?.data || []).map((item: CardType) => ({
            ...item,
            key: item.id,
          })),
        );

        setTotal(response?.total || 0);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  // DELETE
  const { mutate: deleteCardType } = useMutation(client.cardTypes?.delete, {
    onSuccess: () => {
      getCardTypes({ page, limit });
    },
  });

  const onDelete = (id: number) => {
    if (!canDelete) return;
    deleteCardType({ id });
  };

  //  TABLE COLUMNS
  const columns = [
    {
      title: "Product",
      dataIndex: "product",
      width: 180,
      render: (product: any) => (
        <div className="flex flex-col">
          <span className="font-medium">{product?.name || "-"}</span>
          <span className="text-xs text-gray-500">{product?.code || "-"}</span>
        </div>
      ),
    },

    {
      title: "Card Name",
      dataIndex: "name",
      width: 180,
    },

    {
      title: "Card Type ID",
      dataIndex: "bank_type_id",
      width: 120,
    },

    {
      title: "Asset",
      dataIndex: "asset",
      width: 100,
      render: (asset: any) => asset?.code?.toUpperCase() || "-",
    },

    {
      title: "Card Color",
      dataIndex: "color_hex",
      width: 150,
      render: (color: string) => (
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-full border"
            style={{ backgroundColor: color }}
          />
          <span>{color}</span>
        </div>
      ),
    },

    {
      title: "Logo Name",
      dataIndex: "card_logo_name",
      width: 180,
      render: (value: string | null) => value || "-",
    },

    {
      title: "Text Color",
      dataIndex: "card_text_color",
      width: 160,
      render: (color: string) => (
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-full border"
            style={{ backgroundColor: color }}
          />
          <span>{color}</span>
        </div>
      ),
    },

    {
      title: "Action",
      key: "action",
      width: 100,
      fixed: "right" as const,
      render: (_: any, record: CardType) => {
        if (!canEdit && !canDelete) {
          return "—";
        }
        return (
          <div className="flex gap-2">
            {canDelete && (
              <Popconfirm
                title="Delete Card Design"
                description="Are you sure you want to delete this card design?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{
                  style: {
                    backgroundColor: "#ef4444",
                    borderColor: "#ef4444",
                    color: "#fff",
                  },
                }}
              >
                <button>
                  <DeleteIcon className="w-[18px] h-[18px]" />
                </button>
              </Popconfirm>
            )}

            {canEdit && (
              <button
                onClick={() =>
                  navigate(
                    routes.cardTypeEditForm.replace(":id", String(record.id)),
                  )
                }
              >
                <EditIcon className="w-[18px] h-[18px]" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // FETCH
  useEffect(() => {
    if (!canViewCardTypes) return;
    getCardTypes({
      page,
      limit,
      search: debouncedSearch || undefined,
    });
  }, [canViewCardTypes, page, limit, debouncedSearch]);

  // SEARCH DEBOUNCE
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  return (
    <div className="w-full p-4 bg-gray-50">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[28px] font-semibold">Card Design</h2>

        <div className="flex gap-2">
          {/* <Input
            placeholder="Search card design..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          /> */}

          <PermissionGate
            module={[permissionConstants.CARD_DESIGN]}
            action="can_add"
          >
            <button
              className="flex items-center gap-2 px-4 py-2 bg-white rounded hover:bg-[#E6E8EC]"
              onClick={() => navigate(routes.cardTypeForm)}
            >
              <span>Add Card Design</span>
              <PlusIcon className="w-[16px] h-[16px]" />
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* TABLE */}
      {!canViewCardTypes ? (
        <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view card designs.
          </p>
        </div>
      ) : (
        <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={dataSource}
            scroll={{ x: 1300 }}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              position: ["bottomCenter"],
              onChange: (page, pageSize) => {
                setPage(page);
                setLimit(pageSize);
              },
            }}
            bordered
          />
        </div>
      )}
    </div>
  );
};

export default CardTypes;
